import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, MessageCircle, Clock, Users, Instagram, Facebook } from 'lucide-react';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../LoadingSpinner';
import { useLanguage } from '../../contexts/LanguageContext';
import { supabase } from '../../supabaseClient';


const CppContact = () => {
  const { language } = useLanguage();
  const { success, error } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    programmingExperience: 'None',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  
  // Real course information
  const contactInfo = {
    phone: '+962771925032',
    address: 'اطلالة المدينة ستدي سيرفس, Queen Rania St 29-17, Amman',
    instagram: 'https://www.instagram.com/qubit_center',
    facebook: 'https://web.facebook.com/qubitcenter1'
  };
  
  const courseDetails = {
    schedule: 'Sunday – Tuesday – Thursday',
    time: '5:00 – 7:30 PM',
    duration: '30 sessions, 70+ hours, 10 weeks',
    maxStudents: '10 students maximum',
    startDate: 'Wednesday 17/9/2025',
    price: '400 JD or 160 JD per month'
  };
  
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
const handleSubmit = async (e) => {
    e.preventDefault();
   setIsSubmitting(true);

    try {
      // Map form fields -> DB columns
      const row = {
        full_name: (formData.name || '').trim(),
        phone: (formData.phone || '').trim(),
        experience: formData.programmingExperience || 'None',
        comments: formData.message || '',
        course_id: 'cpp-2025-09-06', // change if you like
      };

      console.log('Submitting to Supabase:', row);
      const { error: insertError } = await supabase.from('registrations').insert([row]);

      if (insertError) {
        console.error('Supabase insert error:', insertError);
        throw new Error(insertError.message);
      }

      success(
        language === 'ar'
          ? 'تم إرسال طلبك بنجاح! سنتواصل معك قريباً لتأكيد التسجيل.'
          : "Registration submitted successfully! We'll contact you soon to confirm enrollment."
      );

      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 5000);

      setFormData({
        name: '',
        phone: '',
        programmingExperience: 'None',
        message: '',
      });
    } catch (err) {
      console.error('Submission error:', err);
      error(
        language === 'ar'
          ? 'عذراً، حدث خطأ في إرسال الطلب. يرجى المحاولة مرة أخرى.'
          : 'Sorry, there was an error submitting your request. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    const message = language === 'ar' 
      ? `مرحباً! أريد التسجيل في دورة C++ Programming Basics.
الاسم: ${formData.name || ' '}
الخبرة: ${formData.programmingExperience}
الوقت المفضل: الأحد - الثلاثاء - الخميس (5:00 - 7:30 مساءً)`
      : `Hello! I want to register for C++ Programming Basics course.
Name: ${formData.name || ' '}
Experience: ${formData.programmingExperience}
Schedule: Sunday - Tuesday - Thursday (5:00 - 7:30 PM)`;
    
    const whatsappUrl = `https://wa.me/962771925032?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };
  
  return (
    <section id="contact" className="cpp-section">
      <div className="cpp-container">
        <div className="text-center mb-12">
          <h2 className="cpp-heading-1 mb-4">
            {language === 'ar' ? 'هل أنت مستعد لبدء رحلة C++؟' : 'Ready to Start Your C++ Journey?'}
          </h2>
          <p className="cpp-body-large max-w-3xl mx-auto">
            {language === 'ar' 
              ? 'انضم إلى دفعتنا القادمة وتحول من مبتدئ كامل إلى مبرمج C++ واثق. الأماكن محدودة!'
              : 'Join our next batch and transform from complete beginner to confident C++ programmer. Limited spots available!'
            }
          </p>
        </div>
        
        <div className="cpp-grid-2 max-w-7xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-8">
            {/* Contact Details */}
            <div className="cpp-card">
              <h3 className="cpp-heading-3 text-white mb-6">
                {language === 'ar' ? 'تواصل معنا' : 'Get In Touch'}
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-white font-semibold">
                      {language === 'ar' ? 'الهاتف' : 'Phone'}
                    </div>
                    <div className="text-green-400">{contactInfo.phone}</div>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-white font-semibold">
                      {language === 'ar' ? 'الموقع' : 'Location'}
                    </div>
                    <div className="text-purple-400">{contactInfo.address}</div>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button 
                  onClick={handleWhatsApp}
                  className="flex-1 cpp-btn-secondary"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  {language === 'ar' ? 'واتساب' : 'WhatsApp'}
                </button>
                
                <a 
                  href={contactInfo.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-gradient-to-r from-pink-500 to-purple-600 rounded-lg flex items-center justify-center hover:scale-105 transition-transform"
                >
                  <Instagram className="w-6 h-6 text-white" />
                </a>
                
                <a 
                  href={contactInfo.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center hover:scale-105 transition-transform"
                >
                  <Facebook className="w-6 h-6 text-white" />
                </a>
              </div>
            </div>
            
            {/* Course Details */}
            <div className="cpp-card">
              <h3 className="cpp-heading-3 text-white mb-6">
                {language === 'ar' ? 'تفاصيل الدورة' : 'Course Details'}
              </h3>
              
              <div className="space-y-4">
                <div className="p-4 bg-gray-800/50 rounded-lg border-l-4 border-blue-400">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-white font-semibold">
                      {language === 'ar' ? 'C++ Pathway: خطوتك الأولى في البرمجة' : 'C++ Pathway: Your First Step into Coding'}
                    </h4>
                    <span className="bg-green-500/20 text-green-400 text-xs px-3 py-1 rounded-full">
                      {language === 'ar' ? 'متاح' : 'Available'}
                    </span>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-start gap-2 text-gray-400">
                      <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{contactInfo.address}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400">
                      <Users className="w-4 h-4" />
                      <span>{courseDetails.maxStudents}</span>
                    </div>
                    <div className="text-blue-400 font-medium">
                      {language === 'ar' ? 'يبدأ: ' : 'Starts: '}{courseDetails.startDate}
                    </div>
                    <div className="text-orange-400 font-medium">
                      {language === 'ar' ? 'المدة: ' : 'Duration: '}{courseDetails.duration}
                    </div>
                    <div className="text-green-400 font-bold text-lg mt-3 pt-2 border-t border-gray-700">
                      💰 {courseDetails.price}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Registration Form */}
          <div className="cpp-card">
            <h3 className="cpp-heading-3 text-white mb-6">
              {language === 'ar' ? 'سجل الآن' : 'Register Now'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-white font-semibold mb-2">
                  {language === 'ar' ? 'الاسم الكامل *' : 'Full Name *'}
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full p-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-blue-400 focus:outline-none transition-colors"
                  placeholder={language === 'ar' ? 'أدخل اسمك الكامل' : 'Enter your full name'}
                />
              </div>
              
              <div>
                <label className="block text-white font-semibold mb-2">
                  {language === 'ar' ? 'رقم الهاتف *' : 'Phone Number *'}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full p-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-blue-400 focus:outline-none transition-colors"
                  placeholder="+962 77 192 5032"
                />
              </div>
              
              <div>
                <label className="block text-white font-semibold mb-2">
                  {language === 'ar' ? 'الخبرة البرمجية' : 'Programming Experience'}
                </label>
                <select
                  name="programmingExperience"
                  value={formData.programmingExperience}
                  onChange={handleChange}
                  className="w-full p-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-blue-400 focus:outline-none transition-colors"
                >
                  <option value="None">{language === 'ar' ? 'مبتدئ كامل' : 'Complete Beginner'}</option>
                  <option value="Basic">{language === 'ar' ? 'لدي فكرة بسيطة فقط' : 'I have a basic understanding (just concepts)'}</option>
                  <option value="Some">{language === 'ar' ? 'جرّبت بعض الأساسيات' : 'I’ve tried some basics'}</option>
                  <option value="Experienced">{language === 'ar' ? 'لدي خبرة بلغة برمجة واحدة أو أكثر' : 'I have experience with one or more programming languages'}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-white font-semibold mb-2">
                  {language === 'ar' ? 'أسئلة أو تعليقات' : 'Questions or Comments'}
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full p-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-blue-400 focus:outline-none transition-colors resize-vertical"
                  placeholder={language === 'ar' ? 'أي أسئلة حول الدورة؟' : 'Any questions about the course?'}
                />
              </div>
              
              <button 
                type="submit" 
                disabled={isSubmitting}
                className="cpp-btn-primary w-full relative"
              >
                {isSubmitting ? (
                  <>
                    <LoadingSpinner size="small" className="mr-2" />
                    {language === 'ar' ? 'جارٍ الإرسال...' : 'Submitting...'}
                  </>
                ) : (
                  <>
                    {language === 'ar' ? 'سجل في الدورة' : 'Register for Course'}
                    <Send className="w-5 h-5 ml-2" />
                  </>
                )}
              </button>
            </form>
            
            {/* Success Message */}
            {showSuccessMessage && (
              <div className="mt-4 p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
                <p className="text-green-400 text-center flex items-center justify-center gap-2">
                  <span>✅</span>
                  <span>
                    {language === 'ar' 
                      ? 'تم إرسال طلبك بنجاح! سنتواصل معك قريباً لتأكيد التسجيل.'
                      : 'Registration submitted successfully! We\'ll contact you soon to confirm enrollment.'
                    }
                  </span>
                </p>
              </div>
            )}
            
            <div className="mt-6 p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
              <p className="text-blue-400 text-sm text-center">
                💡 <strong>{language === 'ar' ? 'رسوم التسجيل:' : 'Registration Fee:'}</strong> {courseDetails.price}
                <br />
                <span className="text-gray-400">
                  {language === 'ar' 
                    ? 'يشمل جميع المواد الدراسيةا'
                    : 'Includes all course materials'
                  }
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CppContact;
